import { normalizeRoleKey } from "../constants/contentRoles"

/**
 * Stored in Redux when GET collaborators/.../users/:id returns [] (no row for this user).
 * Also the ROLE_TAB_CONFIG key for those tabs.
 */
export const ACCOUNT_MANAGER_ROLE = "ROLE_ACCOUNT_MANAGER"
export const EXTERNAL_APPROVER_ROLE = "ROLE_EXTERNAL_APPROVER"

/** Drafts (incl. sent-back posts) + pending + scheduled + live — AM, collab publisher, owner, admin. */
const FULL_PROPERTY_REVIEW_TABS = [
  { key: "drafts", label: "Drafts", statusIds: [1, 5] },
  { key: "pending", label: "Pending", statusIds: [4, 8] },
  { key: "scheduled", label: "Scheduled", statusIds: [6] },
  { key: "live", label: "Live", statusIds: [7] },
]

export const ROLE_TAB_CONFIG = {
  [ACCOUNT_MANAGER_ROLE]: {
    tabs: FULL_PROPERTY_REVIEW_TABS,
  },
  ROLE_CREATOR: {
    tabs: [
      { key: "drafts", label: "Drafts", statusIds: [1, 5] },
      { key: "pending", label: "Pending", statusIds: [4] },
      { key: "scheduled", label: "Scheduled", statusIds: [6] },
      { key: "live", label: "Live", statusIds: [7] },
    ],
  },
  ROLE_APPROVER: {
    tabs: [
      { key: "pending", label: "Pending", statusIds: [4] },
      { key: "sent_to_creator", label: "Sent to Creator", statusIds: [5] },
      { key: "scheduled", label: "Scheduled", statusIds: [6] },
      { key: "live", label: "Live", statusIds: [7] },
    ],
  },
  ROLE_PUBLISHER: {
    tabs: [
      { key: "drafts", label: "Drafts", statusIds: [1, 5] },
      { key: "scheduled", label: "Scheduled", statusIds: [6] },
      { key: "live", label: "Live", statusIds: [7] },
    ],
  },
  ROLE_COLLABORATIVE_PUBLISHER: {
    tabs: FULL_PROPERTY_REVIEW_TABS,
  },
}

/**
 * Config key for topic/status tab lists. Roles with no entry (e.g. owner) use account manager tabs.
 */
export function topicTabRole(role) {
  const key = role != null && String(role).trim() !== "" ? String(role).trim() : ""
  if (key && ROLE_TAB_CONFIG[key]) return key
  return ACCOUNT_MANAGER_ROLE
}

/** Internal approver calendar/review UI. */
export function isApproverLikeRole(role) {
  return topicTabRole(role) === "ROLE_APPROVER"
}

/**
 * Status 5 (sent back to creator) shows in Drafts for every role except approvers,
 * who keep a dedicated Sent to Creator tab to track returns.
 */
export function shouldMergeSentToCreatorIntoDrafts(role) {
  return !isApproverLikeRole(role)
}

/** Map post-move target (Redux tab key) → review panel `activeTab` for the current role. */
export function resolveReviewPanelTabAfterPostMove(targetTab, role) {
  const raw = String(targetTab ?? "").trim()
  if (!raw) return null
  const key = raw === "draft" ? "drafts" : raw
  if (key === "sentToCreator" || key === "sent_to_creator") {
    return shouldMergeSentToCreatorIntoDrafts(role) ? "drafts" : "sent_to_creator"
  }
  if (key === "pending_external") return "pending"
  return key
}

/**
 * Pending tab: I Approve + Send Back to Creator.
 * Approver, Account Manager, Collaborative Publisher, Owner, Admin
 * (Owner/Admin use account-manager tabs). Creator never approves.
 */
export function canActOnPendingApprovals(role) {
  if (normalizeRoleKey(role) === "ROLE_CREATOR") return false
  const key = topicTabRole(role)
  const normalized = normalizeRoleKey(role)
  return (
    key === "ROLE_APPROVER" ||
    key === ACCOUNT_MANAGER_ROLE ||
    key === "ROLE_COLLABORATIVE_PUBLISHER" ||
    normalized === "ROLE_OWNER" ||
    normalized === "ROLE_ADMIN"
  )
}

/** Pending tab: Account Manager can edit posts and Save (same draft save flow). */
export function canEditPendingPosts(role) {
  const normalized = normalizeRoleKey(role)
  if (normalized === "ROLE_ACCOUNT_MANAGER" || normalized === "ROLE_MANAGER") return true
  if (!normalized && topicTabRole(role) === ACCOUNT_MANAGER_ROLE) return true
  return false
}

/** Moderator: view calendar/dashboard; no create or review posts in calendar. */
export function isModeratorRole(role) {
  if (role == null || String(role).trim() === "") return false
  const normalized = String(role).trim().replace(/^ROLE_/, "").toUpperCase()
  return normalized === "MODERATOR"
}

/** Single Pending tab (internal + external) — collaborative publisher, account manager, owner, admin. */
export function usesUnifiedPendingTab(role) {
  const key = topicTabRole(role)
  return key === "ROLE_COLLABORATIVE_PUBLISHER" || key === ACCOUNT_MANAGER_ROLE
}

/** Status ids for the unified Pending tab for this role. */
export function getPendingStatusIdsForRole(role) {
  const roleKey = topicTabRole(role)
  const pendingTab = ROLE_TAB_CONFIG[roleKey]?.tabs?.find((t) => t.key === "pending")
  return pendingTab?.statusIds ?? [4]
}

/** Draft tab + create-post confirm: Send for Approval is Creator only. */
export function showsDraftSendForApprovalButton(role) {
  return normalizeRoleKey(role) === "ROLE_CREATOR" || topicTabRole(role) === "ROLE_CREATOR"
}

/** Draft tab: Owner, Admin, and Account Manager can also send a post for approval. */
export function showsDraftManagerSendForApprovalButton(role) {
  const normalized = normalizeRoleKey(role)
  return (
    normalized === "ROLE_OWNER" ||
    normalized === "ROLE_ADMIN" ||
    normalized === "ROLE_ACCOUNT_MANAGER" ||
    normalized === "ROLE_MANAGER"
  )
}

/** Draft tab: every property role except Creator schedules directly. */
export function showsDraftScheduleButton(role) {
  if (showsDraftSendForApprovalButton(role)) return false
  const key = topicTabRole(role)
  const raw = role != null ? String(role).trim() : ""
  return (
    key === "ROLE_PUBLISHER" ||
    key === ACCOUNT_MANAGER_ROLE ||
    key === "ROLE_COLLABORATIVE_PUBLISHER" ||
    raw === "ROLE_OWNER" ||
    raw === "ROLE_ADMIN"
  )
}

/**
 * Scheduled tab: Send back to draft.
 * Hidden only for Creator and Moderator. Every other scheduled-tab role always gets it.
 */
export function showsScheduledSendBackToDraftButton(role) {
  if (isModeratorRole(role)) return false
  const normalized = normalizeRoleKey(role)
  if (normalized === "ROLE_CREATOR" || topicTabRole(role) === "ROLE_CREATOR") return false
  return true
}

/** Review panel comment UI (draft, pending, sent to creator, scheduled, live). All property review roles except moderator. */
export function canViewReviewPanelComments(role) {
  if (isModeratorRole(role)) return false
  const key = topicTabRole(role)
  return (
    key === "ROLE_CREATOR" ||
    key === "ROLE_APPROVER" ||
    key === ACCOUNT_MANAGER_ROLE ||
    key === "ROLE_PUBLISHER" ||
    key === "ROLE_COLLABORATIVE_PUBLISHER"
  )
}

/** Calendar cell CSS key — always a single pending bucket. */
export function resolvePendingTabKey(statusId) {
  const id = Number(statusId)
  if (id === 4 || id === 8) return "pending"
  return null
}

/** Review panel + calendar status tabs — single Pending label, no internal/external split. */
export function getCalendarStatusLegendItems(role) {
  const roleKey = topicTabRole(role)
  return ROLE_TAB_CONFIG[roleKey]?.tabs || []
}
